import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IInvoicesFilters } from 'core/hooks';

import * as S from './FakturowniaInvoices.styled';

export type InvoicesStateChip =
  | 'all'
  /**
   * Spec 0004: «Требуют привязки» — linkable invoices without a взнос yet
   * (backend `needsLinkOnly:true`). Previously Phase 13's payment-less
   * reconcile queue (`unpaidOnly`).
   */
  | 'reconcile'
  /**
   * Spec 0004: «Требует внимания» — invoices whose hidden-Payment
   * auto-provision is blocked (backend `provisionBlockedOnly:true`,
   * e.g. CURRENCY_MISMATCH).
   */
  | 'attention'
  | 'paid'
  | 'overdue'
  | 'rejected';

interface IInvoicesFilterBarProps {
  filters: IInvoicesFilters;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onChange: (next: IInvoicesFilters) => void;
  onReset: () => void;
  /** Phase 8: UI-only toggle — hides corrections AND their parents. */
  hideCorrected: boolean;
  onHideCorrectedChange: (next: boolean) => void;
  /** Phase 10: state chip — one-click access to common payment states. */
  state: InvoicesStateChip;
  onStateChange: (next: InvoicesStateChip) => void;
}

const DIRECTION_TABS = [
  { value: 'all', i18n: 'all' },
  { value: 'issued', i18n: 'issued' },
  { value: 'received', i18n: 'received' },
  { value: 'correction', i18n: 'correction' },
] as const;

/** Chip values map straight to backend filters — `reconcile` →
 *  `needsLinkOnly:true` (spec 0004), `attention` → `provisionBlockedOnly:
 *  true`, `paid/rejected` → `status`, `overdue` → `overdueOnly`. Order:
 *  «Все» on the left as the escape hatch, then states in roughly
 *  daily-importance order. */
const STATE_CHIPS: InvoicesStateChip[] = [
  'all',
  'reconcile',
  'attention',
  'paid',
  'overdue',
  'rejected',
];

const MATCH_OPTIONS = [
  { value: '', i18n: 'matchAll' },
  { value: 'auto', i18n: 'matchAuto' },
  { value: 'manual', i18n: 'matchManual' },
  { value: 'unmatched', i18n: 'matchUnmatched' },
  { value: 'not_applicable', i18n: 'matchNotApplicable' },
] as const;

/**
 * Stateless filter ribbon. Two segment rows now: «Направление» (issued /
 * received / corrections — primary pivot, TZ §3.2) and «Состояние» (Phase 10
 * — quick-access payment-state chips that replaced the old status dropdown).
 * Match is its own dropdown — orthogonal axis about student/group linkage.
 */
export const InvoicesFilterBar = memo(
  ({
    filters,
    searchQuery,
    onSearchChange,
    onChange,
    onReset,
    hideCorrected,
    onHideCorrectedChange,
    state,
    onStateChange,
  }: IInvoicesFilterBarProps) => {
    const { t } = useTranslation();

    const activeDirection =
      filters.kind === 'correction' ? 'correction' : filters.direction || 'all';

    const onDirectionTab = useCallback(
      (value: string) => {
        if (value === 'all') {
          onChange({ ...filters, direction: undefined, kind: undefined });
        } else if (value === 'correction') {
          onChange({ ...filters, direction: undefined, kind: 'correction' });
        } else {
          onChange({ ...filters, direction: value, kind: undefined });
        }
      },
      [filters, onChange],
    );

    return (
      <S.FilterBar>
        <S.SegmentTabs>
          {DIRECTION_TABS.map((tab) => (
            <S.SegmentTab
              key={tab.value}
              type="button"
              active={activeDirection === tab.value}
              onClick={() => onDirectionTab(tab.value)}
            >
              {t(`pages.fakturownia.invoices.filters.direction.${tab.i18n}`)}
            </S.SegmentTab>
          ))}
        </S.SegmentTabs>

        {/* Force the next group onto its own row so the two segment controls
            stack vertically when both render side-by-side. */}
        <div style={{ flexBasis: '100%', height: 0 }} />

        <S.FilterGroup>
          <S.FilterLabel>
            {t('pages.fakturownia.invoices.filters.state.label')}
          </S.FilterLabel>
          <S.SegmentTabs>
            {STATE_CHIPS.map((chip) => (
              <S.SegmentTab
                key={chip}
                type="button"
                active={state === chip}
                onClick={() => onStateChange(chip)}
              >
                {t(`pages.fakturownia.invoices.filters.state.${chip}`)}
              </S.SegmentTab>
            ))}
          </S.SegmentTabs>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>
            {t('pages.fakturownia.invoices.filters.match')}
          </S.FilterLabel>
          <S.NativeSelect
            value={filters.matchStatus || ''}
            onChange={(e) =>
              onChange({
                ...filters,
                matchStatus: e.target.value || undefined,
              })
            }
          >
            {MATCH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(`pages.fakturownia.invoices.filters.${o.i18n}`)}
              </option>
            ))}
          </S.NativeSelect>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.NativeInput
            type="search"
            placeholder={t('pages.fakturownia.invoices.filters.search')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </S.FilterGroup>

        <S.FilterGroup>
          <input
            id="invoices-filter-hide-corrected"
            type="checkbox"
            checked={hideCorrected}
            onChange={(e) => onHideCorrectedChange(e.target.checked)}
            style={{ margin: 0 }}
          />
          <label
            htmlFor="invoices-filter-hide-corrected"
            style={{
              fontSize: 12,
              color: '#0f172a',
              cursor: 'pointer',
              fontWeight: 600,
            }}
            title={t('pages.fakturownia.invoices.hideCorrectedHint')}
          >
            {t('pages.fakturownia.invoices.hideCorrected')}
          </label>
        </S.FilterGroup>

        <S.ResetButton type="button" onClick={onReset}>
          {t('pages.fakturownia.invoices.filters.reset')}
        </S.ResetButton>
      </S.FilterBar>
    );
  },
);
